<script setup lang="ts">
import { computed, inject, onMounted, ref, watch } from 'vue'
import { Decimal } from 'decimal.js'
import { formatting, useLocalization } from '@hantera/portal-app'
import { DecimalInput, DropdownButton, DropdownDivider, DropdownItem, Expandable, TextInput, ToggleSwitch } from '@hantera/design-system'
import type { useJobDefinitionDetailsStore } from '@/stores/jobs/job-definition-details-store'
import type { JobScheduleConcurrency, JobScheduleEntry } from '@/backend/jobs.types'

const t = useLocalization('jobs')
const store = inject<ReturnType<typeof useJobDefinitionDetailsStore>>('jobDefinitionStore')!

const schedules = ref<JobScheduleEntry[]>([])
const suspended = ref(false)
const concurrency = ref<JobScheduleConcurrency>('forbid')
const deadline = ref<Decimal>(new Decimal(300))

const isSaving = ref(false)
const error = ref<string | null>(null)

watch(
  () => store.definition,
  (definition) => {
    if (!definition) return
    schedules.value = (definition.schedules ?? []).map((s) => ({ ...s }))
    suspended.value = definition.scheduleSuspended
    concurrency.value = definition.scheduleConcurrency ?? 'forbid'
    deadline.value = new Decimal(definition.scheduleStartingDeadlineSeconds ?? 300)
  },
  { immediate: true },
)

const normalizeSchedule = (s: JobScheduleEntry) => ({
  cron: s.cron,
  timeZone: s.timeZone ?? null,
})

const isDirty = computed(() => {
  const def = store.definition
  if (!def) return false

  const orig = (def.schedules ?? []).map(normalizeSchedule)
  const cur = schedules.value.map(normalizeSchedule)

  return (
    JSON.stringify(orig) !== JSON.stringify(cur) ||
    suspended.value !== def.scheduleSuspended ||
    concurrency.value !== (def.scheduleConcurrency ?? 'forbid') ||
    deadline.value.toNumber() !== (def.scheduleStartingDeadlineSeconds?.toNumber() ?? 300)
  )
})

const occurrences = computed(() => store.definition?.occurrences ?? [])

const timeZoneFilter = ref('')
const timeZones = Intl.supportedValuesOf('timeZone')
const filteredTimeZones = computed(() =>
  timeZones.filter((tz) => tz.toLowerCase().includes(timeZoneFilter.value.toLowerCase())),
)
const timeZoneLabel = (tz?: string) => tz || t('detail.schedule.utc')

function addSchedule() {
  schedules.value.push({ cron: '' })
}

function removeSchedule(index: number) {
  schedules.value.splice(index, 1)
}

function discard() {
  const def = store.definition
  if (!def) return
  schedules.value = (def.schedules ?? []).map((s) => ({ ...s }))
  suspended.value = def.scheduleSuspended
  concurrency.value = def.scheduleConcurrency ?? 'forbid'
  deadline.value = new Decimal(def.scheduleStartingDeadlineSeconds ?? 300)
}

async function save() {
  isSaving.value = true
  error.value = null
  try {
    await store.saveSchedule({
      schedules: schedules.value.map((s) => ({
        cron: s.cron,
        timeZone: s.timeZone || null,
      })),
      scheduleSuspended: suspended.value,
      scheduleConcurrency: concurrency.value,
      scheduleStartingDeadlineSeconds: Math.round(deadline.value.toNumber()),
    })
    await store.previewOccurrences(10)
  } catch (e) {
    error.value = (e as Error).message
  } finally {
    isSaving.value = false
  }
}

async function resetToDefault() {
  isSaving.value = true
  error.value = null
  try {
    await store.saveSchedule({
      schedules: null,
      scheduleSuspended: null,
      scheduleConcurrency: null,
      scheduleStartingDeadlineSeconds: null,
    })
    await store.previewOccurrences(10)
  } catch (e) {
    error.value = (e as Error).message
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  store.previewOccurrences(10)
})
</script>

<template>
  <div class="schedule-tab">
    <div v-if="error" class="error-box">{{ error }}</div>

    <fieldset>
      <div class="field">
        <label>
          {{ t('detail.schedule.suspended') }}
          <ToggleSwitch v-model="suspended" />
        </label>
      </div>

      <div v-if="schedules.length === 0" class="empty">
        {{ t('detail.schedule.noSchedules') }}
      </div>

      <div v-for="(schedule, index) in schedules" :key="index" class="schedule-row">
        <TextInput v-model="schedule.cron" :placeholder="t('detail.schedule.cronPlaceholder')" />
        <DropdownButton class="timezone-select" :label="timeZoneLabel(schedule.timeZone)">
          <DropdownItem @click="schedule.timeZone = undefined; timeZoneFilter = ''">
            {{ t('detail.schedule.utc') }}
          </DropdownItem>
          <DropdownDivider />
          <div class="timezone-filter">
            <TextInput v-model="timeZoneFilter" type="search" :placeholder="t('detail.schedule.timeZoneFilter')" />
          </div>
          <div class="timezone-scroll">
            <DropdownItem
              v-for="tz in filteredTimeZones"
              :key="tz"
              @click="schedule.timeZone = tz; timeZoneFilter = ''"
            >
              {{ tz }}
            </DropdownItem>
          </div>
        </DropdownButton>
        <button type="button" class="btn-danger" @click="removeSchedule(index)">
          {{ t('detail.schedule.remove') }}
        </button>
      </div>

      <button type="button" class="btn" @click="addSchedule">
        {{ t('detail.schedule.addSchedule') }}
      </button>

      <p class="cron-help">{{ t('detail.schedule.cronHelp') }}</p>
    </fieldset>

    <div class="box-content p">
      <Expandable :label="t('detail.schedule.advancedSettings')">
        <fieldset>
          <div class="field">
            <label for="concurrency">{{ t('detail.schedule.concurrency') }}</label>
            <select v-model="concurrency" name="concurrency">
              <option value="forbid">{{ t('detail.schedule.forbid') }}</option>
              <option value="allow">{{ t('detail.schedule.allow') }}</option>
            </select>
          </div>
          <div class="field">
            <label>{{ t('detail.schedule.deadline') }}</label>
            <DecimalInput v-model="deadline" :min-value="1" />
          </div>
        </fieldset>
        <div class="behaviour-row">
          
          
        </div>

        <div class="behaviour-row">
          
        </div>
      </Expandable>
    </div>

    <div class="box-content p2" v-if="occurrences.length > 0">
      <h3>{{ t('detail.schedule.nextOccurrences') }}</h3>
      <ul class="occurrence-list">
        <li v-for="(occurrence, index) in occurrences" :key="index">
          {{ formatting.dateTime(occurrence) }}
        </li>
      </ul>
    </div>

    <div class="actions">
      <button v-if="store.definition?.scheduleIsOverridden" type="button" class="btn-danger" @click="resetToDefault">
        {{ t('detail.schedule.resetToDefault') }}
      </button>
      <button type="button" class="btn-silent" :disabled="!isDirty" @click="discard">
        {{ t('detail.schedule.discard') }}
      </button>
      <button type="button" class="btn-primary" :disabled="!isDirty || isSaving" @click="save">
        {{ t('detail.schedule.save') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.schedule-tab {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem;
}

.error-box {
  color: hsl(0, 84%, 60%);
  padding: 0.5rem 0.75rem;
  border: 1px solid hsl(0, 84%, 60%);
  border-radius: 6px;
}

.empty {
  color: hsl(var(--color-50));
  font-size: 13px;
  padding: 0.5rem 0;
}

.cron-help {
  margin: 0.5rem 0 0;
  font-size: 12px;
  color: hsl(var(--color-50));
}

.schedule-row {
  display: grid;
  grid-template-columns: 1fr 1fr min-content;
  gap: var(--space-2);
  align-items: center;
  margin-bottom: 0.5rem;
}

.timezone-select {
  width: 100%;
}

.timezone-filter {
  padding: 0.25rem var(--space-1);
}

.timezone-scroll {
  max-height: 400px;
  overflow-y: auto;
}

.occurrence-list {
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 13px;
  color: hsl(var(--color-30));
}

.occurrence-list li {
  padding: 0.25rem 0;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}
</style>